import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import React, { useState } from 'react';
import { useApproveChangeUserRoleMutation } from '../../../api/user.js';
import { useGetUserAssignableRolesQuery } from '../../../api/roles.js';
import { getInitials } from '../../../utils/getInitials.js';
import BaseButton from '../../form/BaseButton.jsx';
import IconCloseFull from '../../images/IconCloseFull.jsx';

const UserApproveConfirmation = React.memo(({ approveVisible, setApproveVisible, user, type }) => {
    const [selectedRole, setSelectedRole] = useState(user.role_id || null);

    const [approveChangeUserRole, { isLoading }] = useApproveChangeUserRoleMutation();

    const {
        data: assignableRoles,
        error: assignableRolesError,
        isLoading: assignableRolesLoading
    } = useGetUserAssignableRolesQuery(user.id);

    const hideDialog = () => {
        setApproveVisible(false);
    };

    const confirmApprove = async () => {
        try {
            await approveChangeUserRole({ id: user.id, data: { role: selectedRole } }).unwrap();
        } catch (error) {
            console.error('Failed to change role:', error);
        }

        setApproveVisible(false);
    };

    const initials = getInitials(user.name);

    const roleOptions =
        assignableRoles?.response?.roles.map((role) => ({
            label: role.name,
            value: role.id
        })) || [];

    if (!approveVisible) return null;

    return (
        <Dialog
            className='rounded-4xl shadow-deep-red-shadow'
            visible={approveVisible}
            style={{ width: '400px' }}
            breakpoints={{ '420px': '320px' }}
            onHide={hideDialog}
            content={() => (
                <div className='flex flex-col justify-center items-center px-6 py-8 pt-12 bg-white rounded-4xl'>
                    <button onClick={hideDialog} className='absolute top-6 right-6'>
                        <IconCloseFull className='fill-theme1-text5' />
                    </button>
                    <div className='rounded-full w-[103px] h-[103px] overflow-hidden mb-[16px]'>
                        <div className='w-full h-full bg-theme1-pale flex items-center justify-center'>
                            <div className='text-theme1-primary font-bold text-[41px]'>{initials}</div>
                        </div>
                    </div>
                    <div className='text-theme1-primary text-[22px] mb-[30px] text-center leading-tight font-bold'>
                        {user.name}
                    </div>

                    <div className='flex justify-content-center w-full mb-9'>
                        {assignableRolesLoading ? (
                            <div>Loading roles...</div>
                        ) : assignableRolesError ? (
                            <div>Error loading roles</div>
                        ) : (
                            <Dropdown
                                value={selectedRole}
                                options={roleOptions}
                                onChange={(e) => setSelectedRole(e.value)}
                                placeholder='Assign a role'
                                className='w-full md:w-14rem  border border-gray-300 rounded-4xl'
                                optionLabel='label'
                            />
                        )}
                    </div>

                    <div className='flex justify-between gap-4 w-full'>
                        <BaseButton
                            className='btn-red-outline w-[47%] flex-shrink-0'
                            textValue='Cancel'
                            onClick={hideDialog}
                        />
                        <BaseButton
                            className={` text-white w-[47%] flex-shrink-0 ${!selectedRole || selectedRole === user.role_id ? 'bg-theme1-pale' : 'bg-theme1-primary'}`}
                            textValue={type === 'pending' ? 'Approve' : 'Change Role'}
                            onClick={confirmApprove}
                            disabled={!selectedRole || selectedRole === user.role_id || isLoading}
                        />
                    </div>
                </div>
            )}
        ></Dialog>
    );
});

UserApproveConfirmation.displayName = 'UserApproveConfirmation';

export default UserApproveConfirmation;
